import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FiLock, FiShield } from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../../../components/ui/Modal";
import Button from "../../../components/ui/Button";
import PermissionPicker from "../../../compoonents/PermissionPicker/PermissionPicker";
import { BUILT_IN_ROLES, Permission, Role } from "../../../types/admin";
import { getRolePermissions, replaceRolePermissions, updateRole } from "../../../services/adminService";
import { getAuthorization } from "../../../services/authService";
import { setAuthorization } from "../../../redux/authSlice";
import { getApiErrorMessage } from "../../../utils/apiError";
import { RoleBadge, roleName } from "./MemberIdentity";
import "./RoleEditor.css";

export default function RoleEditor({ role, catalog, onClose, onUpdated }: { role: Role; catalog: Permission[]; onClose: () => void; onUpdated: () => void }) {
  const dispatch = useDispatch();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || "");
  const [savedName, setSavedName] = useState(role.name);
  const [savedDescription, setSavedDescription] = useState(role.description || "");
  const [keys, setKeys] = useState<string[]>([]);
  const [savedKeys, setSavedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const displayKeys = role.name === "SUPER_ADMIN" ? catalog.map(permission => permission.code) : keys;
  const builtIn = BUILT_IN_ROLES.includes(role.name);
  const permissionChanges = [...keys].sort().join('|') !== [...savedKeys].sort().join('|');
  useEffect(() => {
    const controller = new AbortController(); setLoading(true); setLoaded(false); setError("");
    getRolePermissions(role.id, controller.signal).then(value => {
      if (!controller.signal.aborted) { setKeys(value.permissionKeys); setSavedKeys(value.permissionKeys); setLoaded(true); }
    }).catch(err => { if (!controller.signal.aborted) setError(getApiErrorMessage(err)); }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [role.id, attempt]);
  const save = async (operation: () => Promise<unknown>, success: string) => {
    setBusy(true); setError(""); setMessage("");
    try { await operation(); setMessage(success); toast.success(success); onUpdated(); }
    catch (err) { setError(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };
  return <Modal title={`Manage ${roleName(role.name)}`} description="Set what your team can see and do. Changes apply to everyone with this role."
    onClose={() => { if (!busy) onClose(); }} footer={<>
      <span className="navo-dialog__footer-hint">{loaded ? `${displayKeys.length} permissions selected` : "Loading access settings…"}</span>
      <Button disabled={busy} onClick={onClose}>Done</Button>
      {role.name !== "SUPER_ADMIN" && <Button variant="primary" disabled={busy || !loaded || !permissionChanges} onClick={() => save(async () => {
        await replaceRolePermissions(role.id, keys); setSavedKeys([...keys]); dispatch(setAuthorization(await getAuthorization()));
      }, "Role permissions saved.")}>{busy ? "Saving…" : "Save permissions"}</Button>}
    </>}>
    {error && <p className="integration-error" role="alert">{error}</p>}
    {message && <p className="integration-success" role="status">{message}</p>}
    <form className="role-editor-details" onSubmit={event => { event.preventDefault(); save(async () => {
      await updateRole(role.id, { ...(!builtIn ? { name: name.trim() } : {}), description: description.trim() || null }); setSavedDescription(description.trim()); setSavedName(name.trim());
    }, "Role details saved."); }}>
      <div className="role-editor-details__identity"><span className="role-editor-details__icon"><FiShield aria-hidden="true" /></span><div><span className="role-editor-details__label">Role</span><RoleBadge name={role.name} />{builtIn && <small><FiLock aria-hidden="true" /> Built-in role · name is fixed</small>}</div></div>
      {!builtIn && <label className="navo-field">Role name<input className="navo-field__input" value={name} maxLength={64} required disabled={busy} onChange={event => setName(event.target.value)} /></label>}
      <label className="navo-field">Description<textarea className="navo-field__input" rows={2} value={description} maxLength={255} disabled={busy} onChange={event => setDescription(event.target.value)} placeholder="What should this role be used for?" /></label>
      <div className="role-editor-details__action"><Button type="submit" size="small" disabled={busy || !name.trim() || (description.trim() === savedDescription && (builtIn || name.trim() === savedName))}>Save details</Button></div>
    </form>
    <section className="role-editor-permissions"><div className="role-editor-permissions__heading"><h3>Business permissions</h3><p>Choose access by feature. Only grant what this role needs.</p></div>
      {loading ? <p role="status" className="integration-hint">Loading role permissions…</p> : !loaded ? <Button disabled={busy} onClick={() => setAttempt(value => value + 1)}>Reload permissions</Button> : <>
        {role.name === "SUPER_ADMIN" && <p className="role-editor-fixed"><FiLock aria-hidden="true" /> Super admin has full access. These permissions are fixed.</p>}
        <PermissionPicker catalog={catalog} value={displayKeys} onChange={setKeys} disabled={busy || role.name === "SUPER_ADMIN"} />
      </>}
    </section>
  </Modal>;
}
